<template>
  <BasicTemplate>
    <v-card v-if="view === 'list'">
      <v-card-title class="d-flex flex-wrap align-center">
        <h3 class="mb-0">Custom Categories</h3>
        <v-btn class="ml-4" color="success" :disabled="loading" @click="addCategory">
          Add category
        </v-btn>
      </v-card-title>
      <v-card-text>
        <Loader v-if="loading" />
        <template v-else>
          <v-data-table
              :headers="listHeaders"
              :items="categories"
              item-key="id"
              :items-per-page="25"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
            >
              <template v-slot:item.name="{ item }">
                <span class="cell-name">{{ item.name || item.id }}</span>
              </template>
              <template v-slot:item.dateRange="{ item }">
                {{ formatDateRange(item) }}
              </template>
              <template v-slot:item.gamesCount="{ item }">
                {{ (item.games || []).length }}
              </template>
              <template v-slot:item.homePosition="{ item }">
                {{ formatOrderPosition(item, 0) }}
              </template>
              <template v-slot:item.menuPosition="{ item }">
                {{ formatOrderPosition(item, 1) }}
              </template>
              <template v-slot:item.scheduleStatus="{ item }">
                <v-chip small :color="getCategoryScheduleStatus(item).color" text-color="white">
                  {{ getCategoryScheduleStatus(item).label }}
                </v-chip>
              </template>
              <template v-slot:item.hasPromo="{ item }">
                <v-chip small :color="item.hasPromo ? 'green' : 'grey'" text-color="white">
                  {{ item.hasPromo ? 'Yes' : 'No' }}
                </v-chip>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-icon
                  class="mr-2"
                  color="primary"
                  @click="openEdit(item)"
                >
                  mdi-pencil
                </v-icon>
                <v-icon
                  color="warning"
                  @click="removeCategory(item)"
                >
                  mdi-delete
                </v-icon>
              </template>
            </v-data-table>

            <v-alert v-if="error" type="error" dense class="mt-4">{{ error }}</v-alert>
        </template>
      </v-card-text>
    </v-card>

    <v-card v-else-if="view === 'global-settings'" class="custom-categories-form">
      <v-card-text>
        <h3 class="mb-4">
          <router-link to="/custom-categories">Custom Categories</router-link>
          / Global Settings
        </h3>

        <h4 class="mb-3">Excludes</h4>
        <v-combobox
          v-model="excludes"
          label="Domain excludes"
          multiple
          chips
          clearable
          hide-selected
          small-chips
          deletable-chips
          :items="[]"
          placeholder="Add domains"
          class="mb-6"
        ></v-combobox>

        <h4 class="mb-3">Position on Home Page</h4>
        <div
          v-for="(item, idx) in customOrdering"
          :key="'co-'+idx"
          class="ordering-row d-flex flex-wrap align-end mb-4"
        >
          <v-text-field
            v-model.number="item.pos"
            label="Position"
            type="number"
            dense
            class="ordering-row__pos mr-4"
          />
          <v-combobox
            class="ordering-row__domains flex-grow-1 mr-4"
            :value="item.domains"
            label="Domains"
            @input="updateCustomOrderingDomains(idx, $event)"
            dense
            multiple
            chips
            clearable
            hide-selected
            small-chips
            deletable-chips
            :items="[]"
            placeholder="Add domains"
          />
          <v-icon color="warning" @click="removeCustomOrdering(idx)">
            mdi-delete
          </v-icon>
        </div>

        <div class="d-flex justify-end mb-4">
          <v-btn color="success" @click="addCustomOrdering">Add position</v-btn>
        </div>

        <event-category-save-footer
          :error="error"
          :saving="saving"
          @save="save"
          @cancel="backToList"
        />
      </v-card-text>
    </v-card>

    <v-card v-else-if="view === 'edit'" class="custom-categories-form">
      <v-card-text>
            <h3 class="mb-4">
              <router-link to="/custom-categories">Custom Categories</router-link>
              /
              {{ category.name || category.id || 'New category' }}
            </h3>

            <h4 class="mb-3">Category</h4>

            <div class="multiple-inputs-wrap category-schedule-row">
              <div class="category-schedule-row__column category-id-column">
                <div class="form__input-box">
                  <label for="category-id">ID</label>
                  <input
                    id="category-id"
                    type="text"
                    class="form__input"
                    v-model="category.id"
                    :readonly="editingCategoryIndex !== null && editingCategoryIndex < originalCategoriesCount"
                  />
                </div>
              </div>
              <div class="category-schedule-row__column">
                <div class="form__input-box">
                  <label for="category-name">Name</label>
                  <input
                    id="category-name"
                    type="text"
                    class="form__input"
                    :value="category.name"
                    @input="updateNameField($event.target.value)"
                  />
                </div>
              </div>
              <div class="category-schedule-row__column">
                <div class="form__input-box">
                  <label for="category-description">Description</label>
                  <input
                    id="category-description"
                    type="text"
                    class="form__input"
                    :value="category.description"
                    @input="updateDescriptionField($event.target.value)"
                  />
                </div>
              </div>
            </div>

            <div class="multiple-inputs-wrap category-schedule-row">
              <div v-if="localesNameWithoutEn.length" class="category-schedule-row__column">
                <div class="form__input-box">
                  <span class="form__field-label" :id="'category-name-locales-label'">Name Locales</span>
                  <v-tabs v-model="localesTabName" background-color="transparent" grow :aria-labelledby="'category-name-locales-label'">
                    <v-tab v-for="lang in localesNameWithoutEn" :key="'name-tab-'+lang">{{ (lang || '').toUpperCase() }}</v-tab>
                  </v-tabs>
                  <v-tabs-items v-model="localesTabName">
                    <v-tab-item v-for="lang in localesNameWithoutEn" :key="'name-tab-item-'+lang">
                      <div class="form__input-box">
                        <label :for="'category-name-locale-' + lang">Name {{ (lang || '').toUpperCase() }}</label>
                        <input
                          :id="'category-name-locale-' + lang"
                          type="text"
                          class="form__input"
                          :placeholder="lang"
                          :value="category.nameLocales[lang]"
                          @input="updateNameLocale(lang, $event.target.value)"
                        />
                      </div>
                    </v-tab-item>
                  </v-tabs-items>
                </div>
              </div>
              <div v-if="localesDescriptionWithoutEn.length" class="category-schedule-row__column">
                <div class="form__input-box">
                  <span class="form__field-label" :id="'category-description-locales-label'">Description Locales</span>
                  <v-tabs v-model="localesTabDescription" background-color="transparent" grow :aria-labelledby="'category-description-locales-label'">
                    <v-tab v-for="lang in localesDescriptionWithoutEn" :key="'desc-tab-'+lang">{{ (lang || '').toUpperCase() }}</v-tab>
                  </v-tabs>
                  <v-tabs-items v-model="localesTabDescription">
                    <v-tab-item v-for="lang in localesDescriptionWithoutEn" :key="'desc-tab-item-'+lang">
                      <div class="form__input-box">
                        <label :for="'category-description-locale-' + lang">Description {{ (lang || '').toUpperCase() }}</label>
                        <input
                          :id="'category-description-locale-' + lang"
                          type="text"
                          class="form__input"
                          :placeholder="lang"
                          :value="category.descriptionLocales[lang]"
                          @input="updateDescriptionLocale(lang, $event.target.value)"
                        />
                      </div>
                    </v-tab-item>
                  </v-tabs-items>
                </div>
              </div>
            </div>

            <div class="multiple-inputs-wrap category-schedule-row">
              <div class="category-schedule-row__column">
                <div class="form__input-box">
                  <label for="category-start-date">Start Date</label>
                  <input
                    id="category-start-date"
                    type="date"
                    class="form__input"
                    :value="startDateInput"
                    @input="updateStartDate($event.target.value)"
                  />
                </div>
                <div class="form__input-box">
                  <label for="category-end-date">End Date</label>
                  <input
                    id="category-end-date"
                    type="date"
                    class="form__input"
                    :value="endDateInput"
                    @input="updateEndDate($event.target.value)"
                  />
                </div>
              </div>
              <div class="category-schedule-row__column">
                <div class="form__input-box">
                  <label for="category-home-position">Home Position</label>
                  <input
                    id="category-home-position"
                    type="number"
                    class="form__input"
                    :value="orderPositionValue(0)"
                    @input="updateOrderPosition(0, $event.target.value)"
                  />
                </div>
                <div class="form__input-box">
                  <label for="category-top-menu-position">Top Menu Position</label>
                  <input
                    id="category-top-menu-position"
                    type="number"
                    class="form__input"
                    :value="orderPositionValue(1)"
                    @input="updateOrderPosition(1, $event.target.value)"
                  />
                </div>
              </div>
            </div>

            <div class="multiple-inputs-wrap">
              <div class="form__input-box">
                <v-combobox
                  v-model="eventGamesSlugs"
                  label="Games"
                  aria-describedby="category-games-hint"
                  multiple
                  chips
                  clearable
                  hide-selected
                  small-chips
                  deletable-chips
                  :items="[]"
                  placeholder="Add game slugs"
                ></v-combobox>
                <p id="category-games-hint" class="text-caption grey--text mb-2">
                  If no games are selected, the category may not appear on the site.
                </p>
              </div>
              <div class="form__input-box">
                <v-combobox
                  v-model="category.excludes"
                  label="Exclude domains"
                  multiple
                  chips
                  clearable
                  hide-selected
                  small-chips
                  deletable-chips
                  :items="[]"
                  placeholder="Add domains"
                ></v-combobox>
              </div>
            </div>

            <h3 class="main-title bottom-row">Promo Banner</h3>

            <div class="multiple-inputs-wrap">
              <div class="form__input-box form__input-box--small">
                <v-switch
                  v-model="category.hasPromo"
                  inset
                  label="Has promo banner"
                  @change="onHasPromoChange"
                ></v-switch>
              </div>
            </div>

            <template v-if="category.hasPromo">
              <div class="multiple-inputs-wrap">
                <div class="form__input-box">
                  <v-combobox
                    v-model="category.promoBannerExcludes"
                    label="Promo Banner Excludes"
                    multiple
                    chips
                    clearable
                    hide-selected
                    small-chips
                    deletable-chips
                    :items="[]"
                    placeholder="Add domains"
                  ></v-combobox>
                </div>
              </div>

              <div class="multiple-inputs-wrap category-schedule-row">
              <div class="category-schedule-row__column">
                <div class="form__input-box">
                  <span class="form__field-label" id="category-promo-title-label">Title</span>
                  <v-tabs v-model="localesTabTitle" background-color="transparent" grow aria-labelledby="category-promo-title-label">
                    <v-tab v-for="lang in localesPromoTitle" :key="'title-tab-'+lang">{{ (lang || '').toUpperCase() }}</v-tab>
                  </v-tabs>
                  <v-tabs-items v-model="localesTabTitle">
                    <v-tab-item v-for="lang in localesPromoTitle" :key="'title-tab-item-'+lang">
                      <div class="form__input-box">
                        <label :for="'category-promo-title-locale-' + lang">Title {{ (lang || '').toUpperCase() }}</label>
                        <input :id="'category-promo-title-locale-' + lang" type="text" class="form__input" :placeholder="lang" v-model="category.promoBanner.titleLocales[lang]" />
                      </div>
                    </v-tab-item>
                  </v-tabs-items>
                </div>
              </div>
              <div class="category-schedule-row__column">
                <div class="form__input-box">
                  <span class="form__field-label" id="category-promo-subtitle-label">Subtitle</span>
                  <v-tabs v-model="localesTabSubtitle" background-color="transparent" grow aria-labelledby="category-promo-subtitle-label">
                    <v-tab v-for="lang in localesPromoSubtitle" :key="'subtitle-tab-'+lang">{{ (lang || '').toUpperCase() }}</v-tab>
                  </v-tabs>
                  <v-tabs-items v-model="localesTabSubtitle">
                    <v-tab-item v-for="lang in localesPromoSubtitle" :key="'subtitle-tab-item-'+lang">
                      <div class="form__input-box">
                        <label :for="'category-promo-subtitle-locale-' + lang">Subtitle {{ (lang || '').toUpperCase() }}</label>
                        <input :id="'category-promo-subtitle-locale-' + lang" type="text" class="form__input" :placeholder="lang" v-model="category.promoBanner.subtitleLocales[lang]" />
                      </div>
                    </v-tab-item>
                  </v-tabs-items>
                </div>
              </div>
            </div>

            <div class="multiple-inputs-wrap">
              <div class="form__input-box">
                <span class="form__field-label" id="category-promo-button-label">Button</span>
                <v-tabs v-model="localesTabButton" background-color="transparent" grow aria-labelledby="category-promo-button-label">
                  <v-tab v-for="lang in localesPromoButton" :key="'button-tab-'+lang">{{ (lang || '').toUpperCase() }}</v-tab>
                </v-tabs>
                <v-tabs-items v-model="localesTabButton">
                  <v-tab-item v-for="lang in localesPromoButton" :key="'button-tab-item-'+lang">
                    <div class="form__input-box">
                      <label :for="'category-promo-button-locale-' + lang">Button {{ (lang || '').toUpperCase() }}</label>
                      <input :id="'category-promo-button-locale-' + lang" type="text" class="form__input" :placeholder="lang" v-model="category.promoBanner.buttonLocales[lang]" />
                    </div>
                  </v-tab-item>
                </v-tabs-items>
              </div>
            </div>

            <div class="multiple-inputs-wrap category-schedule-row">
              <div class="category-schedule-row__column">
                <div class="form__input-box">
                  <label for="category-promo-custom-event-game-thumb">Custom Event Game Thumb</label>
                  <input id="category-promo-custom-event-game-thumb" type="text" class="form__input" v-model="category.promoBanner.customEventGameThumb" readonly />
                  <v-file-input
                    v-model="uploadFiles.customEventGameThumb"
                    dense
                    hide-details
                    prepend-icon="mdi-upload"
                    accept="image/*"
                    label="Upload new Custom Event Game Thumb (optional)"
                    @change="onFileChanged('customEventGameThumb')"
                  ></v-file-input>
                  <div v-if="getPreviewUrl('customEventGameThumb') || category.promoBanner.customEventGameThumb" class="image-preview">
                    <img
                      :src="getPreviewUrl('customEventGameThumb') || category.promoBanner.customEventGameThumb"
                      alt="Custom Event Game Thumb preview"
                      class="preview-image"
                    />
                  </div>
                </div>
                <div class="form__input-box">
                  <label for="category-promo-custom-event-game-thumb-webp">Custom Event Game Thumb (WebP)</label>
                  <input id="category-promo-custom-event-game-thumb-webp" type="text" class="form__input" v-model="category.promoBanner.customEventGameThumbWebp" readonly />
                  <v-file-input
                    v-model="uploadFiles.customEventGameThumbWebp"
                    dense
                    hide-details
                    prepend-icon="mdi-upload"
                    accept="image/webp,image/*"
                    label="Upload WebP (optional)"
                    @change="onFileChanged('customEventGameThumbWebp')"
                  ></v-file-input>
                  <div v-if="getPreviewUrl('customEventGameThumbWebp') || category.promoBanner.customEventGameThumbWebp" class="image-preview">
                    <img
                      :src="getPreviewUrl('customEventGameThumbWebp') || category.promoBanner.customEventGameThumbWebp"
                      alt="Custom Event Game Thumb WebP preview"
                      class="preview-image"
                    />
                  </div>
                </div>
              </div>
              <div class="category-schedule-row__column">
                <div class="form__input-box">
                  <label for="category-promo-thumb-path">Promo Thumb Path</label>
                  <input id="category-promo-thumb-path" type="text" class="form__input" v-model="category.promoBanner.promoThumbPath" readonly />
                  <v-file-input
                    v-model="uploadFiles.promoThumbPath"
                    dense
                    hide-details
                    prepend-icon="mdi-upload"
                    accept="image/*"
                    label="Upload Promo Thumb (optional)"
                    @change="onFileChanged('promoThumbPath')"
                  ></v-file-input>
                  <div v-if="getPreviewUrl('promoThumbPath') || category.promoBanner.promoThumbPath" class="image-preview">
                    <img
                      :src="getPreviewUrl('promoThumbPath') || category.promoBanner.promoThumbPath"
                      alt="Promo Thumb preview"
                      class="preview-image"
                    />
                  </div>
                </div>
                <div class="form__input-box">
                  <label for="category-promo-thumb-path-webp">Promo Thumb Path (WebP)</label>
                  <input id="category-promo-thumb-path-webp" type="text" class="form__input" v-model="category.promoBanner.promoThumbPathWebP" readonly />
                  <v-file-input
                    v-model="uploadFiles.promoThumbPathWebP"
                    dense
                    hide-details
                    prepend-icon="mdi-upload"
                    accept="image/webp,image/*"
                    label="Upload Promo Thumb WebP (optional)"
                    @change="onFileChanged('promoThumbPathWebP')"
                  ></v-file-input>
                  <div v-if="getPreviewUrl('promoThumbPathWebP') || category.promoBanner.promoThumbPathWebP" class="image-preview">
                    <img
                      :src="getPreviewUrl('promoThumbPathWebP') || category.promoBanner.promoThumbPathWebP"
                      alt="Promo Thumb WebP preview"
                      class="preview-image"
                    />
                  </div>
                </div>
              </div>
            </div>
            </template>

            <event-category-save-footer
              :error="error"
              :saving="saving"
              @save="save"
              @cancel="backToList"
            />
      </v-card-text>
    </v-card>
  </BasicTemplate>
</template>

<script>
  import BasicTemplate from '../../templates/basic.vue'
  import Loader from '../Loader/Loader.vue'
  import EventCategorySaveFooter from './EventCategorySaveFooter.vue'
  import { EventCategoryService } from '../../services/EventCategoryService'
  import { ClearCacheService } from '../../services/ClearCacheService'
  import {
    getCategoryScheduleStatus,
    inferHasPromo,
    normalizeCategoryOrder,
    resolveCategoryName,
    resolveCategoryDescription
  } from './eventCategoryConfigHelpers'

  const DEFAULT_LOCALES = ['en', 'fr', 'it', 'de', 'es']

  function defaultPromoBanner () {
    return {
      customEventGameThumb: '',
      customEventGameThumbWebp: '',
      promoThumbPath: '',
      promoThumbPathWebP: '',
      titleLocales: {},
      subtitleLocales: {},
      buttonLocales: {}
    }
  }

  function defaultCategory () {
    return {
      id: '',
      isCategory: true,
      name: '',
      nameLocales: {},
      description: '',
      descriptionLocales: {},
      order: [],
      startDate: [],
      endDate: [],
      promoBanner: defaultPromoBanner(),
      hasPromo: true,
      promoBannerExcludes: [],
      excludes: [],
      games: []
    }
  }

  function dateArrayToInput (arr) {
    if (!Array.isArray(arr) || arr.length < 3) return ''
    const [day, month, year] = arr
    if (!day || !month || !year) return ''
    const pad = n => String(n).padStart(2, '0')
    return `${year}-${pad(month)}-${pad(day)}`
  }

  function dateArrayToDisplay (arr) {
    if (!Array.isArray(arr) || arr.length < 3) return ''
    const [day, month, year] = arr
    if (!day || !month || !year) return ''
    const pad = n => String(n).padStart(2, '0')
    return `${pad(day)}-${pad(month)}-${year}`
  }

  function dateInputToArray (value) {
    if (!value) return []
    const parts = value.split('-').map(Number)
    if (parts.length < 3) return []
    const [year, month, day] = parts
    return [day, month, year]
  }

  export default {
    name: 'EventCategoryConfig',
    components: { BasicTemplate, Loader, EventCategorySaveFooter },
    data () {
      return {
        view: 'list',
        editingCategoryIndex: null,
        originalCategoriesCount: 0,
        loading: true,
        saving: false,
        error: '',
        service: null,
        version: 1,
        categories: [],
        category: defaultCategory(),
        eventGamesSlugs: [],
        excludes: [],
        customOrdering: [],
        localesTabName: 0,
        localesTabDescription: 0,
        localesTabTitle: 0,
        localesTabSubtitle: 0,
        localesTabButton: 0,
        listHeaders: [
          { text: 'ID', value: 'id', width: '180px' },
          { text: 'Name', value: 'name' },
          { text: 'Date range', value: 'dateRange', sortable: false },
          { text: 'Games', value: 'gamesCount', sortable: false, width: '90px' },
          { text: 'Home position', value: 'homePosition', sortable: false, width: '110px' },
          { text: 'Menu position', value: 'menuPosition', sortable: false, width: '120px' },
          { text: 'Promo', value: 'hasPromo', sortable: false, width: '90px' },
          { text: 'On site', value: 'scheduleStatus', sortable: false, width: '120px' },
          { text: 'Actions', value: 'actions', sortable: false, width: '160px', align: 'center' }
        ],
        uploadFiles: {
          customEventGameThumb: null,
          customEventGameThumbWebp: null,
          promoThumbPath: null,
          promoThumbPathWebP: null
        }
      }
    },
    async created () {
      const ctx = (this.$msal || this.msal) ? { $msal: this.$msal, msal: this.msal } : null
      this.service = new EventCategoryService(ctx)
      await this.load()
    },
    watch: {
      '$route' () {
        this.syncViewFromRoute()
      }
    },
    beforeDestroy () {
      this.revokeAllPreviewUrls()
    },
    computed: {
      startDateInput () {
        return dateArrayToInput(this.category.startDate)
      },
      endDateInput () {
        return dateArrayToInput(this.category.endDate)
      },
      localesName () {
        const keys = Object.keys(this.category?.nameLocales || {})
        return keys.length ? keys : DEFAULT_LOCALES
      },
      localesNameWithoutEn () {
        return this.localesName.filter(lang => lang !== 'en')
      },
      localesDescription () {
        const keys = Object.keys(this.category?.descriptionLocales || {})
        return keys.length ? keys : DEFAULT_LOCALES
      },
      localesDescriptionWithoutEn () {
        return this.localesDescription.filter(lang => lang !== 'en')
      },
      localesPromoTitle () {
        const keys = Object.keys(this.category?.promoBanner?.titleLocales || {})
        return keys.length ? keys : DEFAULT_LOCALES
      },
      localesPromoSubtitle () {
        const keys = Object.keys(this.category?.promoBanner?.subtitleLocales || {})
        return keys.length ? keys : DEFAULT_LOCALES
      },
      localesPromoButton () {
        const keys = Object.keys(this.category?.promoBanner?.buttonLocales || {})
        return keys.length ? keys : DEFAULT_LOCALES
      }
    },
    methods: {
      getCategoryScheduleStatus,
      applyConfig (cfg) {
        if (!cfg) return

        if (Array.isArray(cfg.categories)) {
          this.version = cfg.version ?? 1
          this.categories = cfg.categories.map(c => this.normalizeCategoryFromApi(c))
          this.excludes = Array.isArray(cfg.excludes) ? cfg.excludes.slice() : []
          this.customOrdering = Array.isArray(cfg.customOrdering) ? cfg.customOrdering.slice() : []
        } else if (Array.isArray(cfg.common) && cfg.common.length > 0) {
          this.version = 1
          this.categories = [this.migrateLegacyCategory(cfg.common[0])]
          this.excludes = Array.isArray(cfg.excludes) ? cfg.excludes.slice() : []
          this.customOrdering = Array.isArray(cfg.customOrdering) ? cfg.customOrdering.slice() : []
        } else {
          this.version = 1
          this.categories = []
          this.excludes = []
          this.customOrdering = []
        }

        this.originalCategoriesCount = this.categories.length
      },
      migrateLegacyCategory (entry) {
        const ensure = (obj, key, fallback) => (obj && typeof obj[key] !== 'undefined') ? obj[key] : fallback
        const promo = entry.eventPromoBanner || entry.eventPromoBannerTURNOFF || {}
        return {
          id: ensure(entry, 'id', 'seasonal-games'),
          isCategory: true,
          name: ensure(entry, 'eventName', ''),
          nameLocales: ensure(entry, 'eventNameLocales', {}),
          description: ensure(entry, 'eventDescription', ''),
          descriptionLocales: ensure(entry, 'eventDescriptionLocales', {}),
          order: ensure(entry, 'eventCategoryOrder', []),
          startDate: this.coerceLegacyDates(entry.startDate),
          endDate: this.coerceLegacyDates(entry.endDate),
          promoBanner: {
            customEventGameThumb: ensure(promo, 'customEventGameThumb', ''),
            customEventGameThumbWebp: ensure(promo, 'customEventGameThumbWebp', ''),
            promoThumbPath: ensure(promo, 'promoThumbPath', ''),
            promoThumbPathWebP: ensure(promo, 'promoThumbPathWebP', ''),
            titleLocales: ensure(promo, 'titleLocales', {}),
            subtitleLocales: ensure(promo, 'subtitleLocales', {}),
            buttonLocales: ensure(promo, 'buttonLocales', {})
          },
          hasPromo: inferHasPromo(entry),
          promoBannerExcludes: ensure(entry, 'eventPromoBannerExcludes', []),
          excludes: Array.isArray(entry.excludes) ? entry.excludes.slice() : [],
          games: ensure(entry, 'eventGames', [])
        }
      },
      coerceLegacyDates (dates) {
        if (!Array.isArray(dates) || dates.length === 0) return []
        const first = dates[0]
        if (Array.isArray(first) && first.length >= 3) return first
        if (typeof first === 'string' && first.includes('-')) {
          return dateInputToArray(first)
        }
        return []
      },
      normalizeCategoryFromApi (raw) {
        const ensure = (obj, key, fallback) => (obj && typeof obj[key] !== 'undefined') ? obj[key] : fallback
        const promo = raw.promoBanner || raw.eventPromoBanner || {}
        return {
          id: ensure(raw, 'id', ''),
          isCategory: true,
          name: ensure(raw, 'name', ensure(raw, 'eventName', '')),
          nameLocales: ensure(raw, 'nameLocales', ensure(raw, 'eventNameLocales', {})),
          description: ensure(raw, 'description', ensure(raw, 'eventDescription', '')),
          descriptionLocales: ensure(raw, 'descriptionLocales', ensure(raw, 'eventDescriptionLocales', {})),
          order: ensure(raw, 'order', ensure(raw, 'eventCategoryOrder', [])),
          startDate: Array.isArray(raw.startDate) ? raw.startDate.slice() : [],
          endDate: Array.isArray(raw.endDate) ? raw.endDate.slice() : [],
          promoBanner: {
            customEventGameThumb: ensure(promo, 'customEventGameThumb', ''),
            customEventGameThumbWebp: ensure(promo, 'customEventGameThumbWebp', ''),
            promoThumbPath: ensure(promo, 'promoThumbPath', ''),
            promoThumbPathWebP: ensure(promo, 'promoThumbPathWebP', ''),
            titleLocales: ensure(promo, 'titleLocales', {}),
            subtitleLocales: ensure(promo, 'subtitleLocales', {}),
            buttonLocales: ensure(promo, 'buttonLocales', {})
          },
          hasPromo: inferHasPromo(raw),
          promoBannerExcludes: ensure(raw, 'promoBannerExcludes', ensure(raw, 'eventPromoBannerExcludes', [])),
          excludes: Array.isArray(raw.excludes) ? raw.excludes.slice() : [],
          games: ensure(raw, 'games', ensure(raw, 'eventGames', []))
        }
      },
      buildCategoryPayload (cat, gamesSlugs) {
        const slugs = gamesSlugs || (cat.games || []).map(g => g.slug)
        const games = slugs.filter(Boolean).map(slug => ({ slug }))
        const order = normalizeCategoryOrder(cat.order)
        return {
          id: cat.id,
          isCategory: true,
          name: resolveCategoryName(cat),
          nameLocales: cat.nameLocales || {},
          description: resolveCategoryDescription(cat),
          descriptionLocales: cat.descriptionLocales || {},
          order,
          startDate: Array.isArray(cat.startDate) ? cat.startDate : [],
          endDate: Array.isArray(cat.endDate) ? cat.endDate : [],
          promoBanner: { ...cat.promoBanner },
          hasPromo: !!cat.hasPromo,
          promoBannerExcludes: cat.hasPromo ? (cat.promoBannerExcludes || []) : [],
          excludes: Array.isArray(cat.excludes)
            ? cat.excludes.map(d => String(d).trim()).filter(Boolean)
            : [],
          games
        }
      },
      onHasPromoChange (value) {
        if (!value) {
          this.$set(this.category, 'promoBannerExcludes', [])
        }
      },
      buildPayload () {
        let categoriesPayload = this.categories.map(c => this.buildCategoryPayload(c))

        if (this.view === 'edit') {
          const edited = this.buildCategoryPayload(this.category, this.eventGamesSlugs)
          if (this.editingCategoryIndex === null) {
            categoriesPayload = [...categoriesPayload, edited]
          } else {
            categoriesPayload = categoriesPayload.map((c, i) =>
              i === this.editingCategoryIndex ? edited : c
            )
          }
        }

        return {
          version: this.version,
          excludes: this.excludes || [],
          customOrdering: (this.customOrdering || []).map(item => ({
            pos: item.pos || 0,
            domains: Array.isArray(item.domains) ? item.domains : []
          })),
          categories: categoriesPayload
        }
      },
      validatePayload (payload) {
        const errors = []
        if (!Array.isArray(payload.categories)) {
          errors.push('categories must be an array')
          return errors
        }
        payload.categories.forEach((c, i) => {
          if (!c.id || typeof c.id !== 'string') errors.push(`categories[${i}].id is required`)
          if (typeof c.name !== 'string') errors.push(`categories[${i}].name must be string`)
          if (typeof c.description !== 'string') errors.push(`categories[${i}].description must be string`)
          if (!Array.isArray(c.games)) errors.push(`categories[${i}].games must be an array`)
        })
        const ids = payload.categories.map(c => c.id)
        const dupes = ids.filter((id, idx) => ids.indexOf(id) !== idx)
        if (dupes.length) errors.push(`duplicate category ids: ${[...new Set(dupes)].join(', ')}`)
        return errors
      },
      formatDateRange (cat) {
        const start = dateArrayToDisplay(cat.startDate)
        const end = dateArrayToDisplay(cat.endDate)
        if (!start && !end) return '—'
        if (start && end) return `${start} — ${end}`
        return start || end
      },
      formatOrderPosition (cat, index) {
        const value = Array.isArray(cat.order) ? cat.order[index] : undefined
        if (value === undefined || value === null || value === '') return '—'
        return String(value)
      },
      openGlobalSettingsState () {
        this.view = 'global-settings'
        this.editingCategoryIndex = null
        this.error = ''
      },
      openEdit (item) {
        this.$router.push({ path: '/custom-categories', query: { categoryId: item.id } })
      },
      openEditState (item) {
        const idx = this.categories.findIndex(c => c.id === item.id)
        this.editingCategoryIndex = idx >= 0 ? idx : null
        this.category = JSON.parse(JSON.stringify(idx >= 0 ? this.categories[idx] : item))
        this.eventGamesSlugs = (this.category.games || []).map(g => g.slug)
        this.resetUploads()
        this.view = 'edit'
        this.error = ''
        this.$nextTick(() => this.ensureLocaleKeys())
      },
      addCategory () {
        this.$router.push({ path: '/custom-categories', query: { new: '1' } })
      },
      addCategoryState () {
        const cat = defaultCategory()
        cat.id = `category-${Date.now()}`
        this.editingCategoryIndex = null
        this.category = cat
        this.eventGamesSlugs = []
        this.resetUploads()
        this.view = 'edit'
        this.error = ''
        this.$nextTick(() => this.ensureLocaleKeys())
      },
      syncViewFromRoute () {
        if (this.$route.query.globalSettings) {
          if (this.view !== 'global-settings') {
            this.openGlobalSettingsState()
          }
          return
        }

        const categoryId = this.$route.query.categoryId
        const isNew = this.$route.query.new

        if (!categoryId && !isNew) {
          this.backToListState()
          return
        }

        if (isNew) {
          if (this.view !== 'edit' || this.editingCategoryIndex !== null) {
            this.addCategoryState()
          }
          return
        }

        const item = this.categories.find(c => c.id === categoryId)
        if (item) {
          if (this.view === 'edit' && this.category.id === item.id) {
            return
          }
          this.openEditState(item)
        } else if (!this.loading) {
          this.backToListState()
          if (this.$route.path === '/custom-categories') {
            this.$router.replace({ path: '/custom-categories' })
          }
        }
      },
      async removeCategory (item) {
        const confirmed = await this.$swal.fire({
          title: 'Delete category?',
          text: `"${item.name || item.id}" will be removed from the config.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          confirmButtonText: 'Delete'
        })
        if (!confirmed.isConfirmed) return
        const backup = this.categories.slice()
        this.categories = backup.filter(c => c.id !== item.id)
        await this.save()
        if (this.error) {
          this.categories = backup
        }
      },
      backToList () {
        if (this.$route.query.categoryId || this.$route.query.new || this.$route.query.globalSettings) {
          this.$router.push({ path: '/custom-categories' })
        } else {
          this.backToListState()
        }
      },
      backToListState () {
        this.view = 'list'
        this.editingCategoryIndex = null
        this.category = defaultCategory()
        this.eventGamesSlugs = []
        this.resetUploads()
        this.error = ''
      },
      ensureLocaleKeys () {
        const ensureOn = (obj, langs) => {
          langs.forEach(lang => {
            if (obj && typeof obj[lang] === 'undefined') {
              this.$set(obj, lang, '')
            }
          })
        }
        ensureOn(this.category.nameLocales, this.localesName)
        this.syncNameFromLocales()
        ensureOn(this.category.descriptionLocales, this.localesDescription)
        this.syncDescriptionFromLocales()
        ensureOn(this.category.promoBanner.titleLocales, this.localesPromoTitle)
        ensureOn(this.category.promoBanner.subtitleLocales, this.localesPromoSubtitle)
        ensureOn(this.category.promoBanner.buttonLocales, this.localesPromoButton)
      },
      syncNameFromLocales () {
        if (!this.category.nameLocales) {
          this.$set(this.category, 'nameLocales', {})
        }
        const en = this.category.nameLocales.en
        if (en !== undefined && en !== null && String(en).trim() !== '') {
          this.$set(this.category, 'name', String(en))
          return
        }
        if (this.category.name) {
          this.$set(this.category.nameLocales, 'en', this.category.name)
        }
      },
      updateNameField (value) {
        this.$set(this.category, 'name', value)
        this.$set(this.category.nameLocales, 'en', value)
      },
      updateNameLocale (lang, value) {
        this.$set(this.category.nameLocales, lang, value)
        if (lang === 'en') {
          this.$set(this.category, 'name', value)
        }
      },
      syncDescriptionFromLocales () {
        if (!this.category.descriptionLocales) {
          this.$set(this.category, 'descriptionLocales', {})
        }
        const en = this.category.descriptionLocales.en
        if (en !== undefined && en !== null && String(en).trim() !== '') {
          this.$set(this.category, 'description', String(en))
          return
        }
        if (this.category.description) {
          this.$set(this.category.descriptionLocales, 'en', this.category.description)
        }
      },
      updateDescriptionField (value) {
        this.$set(this.category, 'description', value)
        this.$set(this.category.descriptionLocales, 'en', value)
      },
      updateDescriptionLocale (lang, value) {
        this.$set(this.category.descriptionLocales, lang, value)
        if (lang === 'en') {
          this.$set(this.category, 'description', value)
        }
      },
      updateStartDate (value) {
        this.$set(this.category, 'startDate', dateInputToArray(value))
      },
      updateEndDate (value) {
        this.$set(this.category, 'endDate', dateInputToArray(value))
      },
      orderPositionValue (index) {
        const value = Array.isArray(this.category.order) ? this.category.order[index] : undefined
        return value === undefined || value === null ? '' : value
      },
      updateOrderPosition (index, value) {
        const order = Array.isArray(this.category.order) ? this.category.order.slice(0, 2) : []
        while (order.length < 2) {
          order.push('')
        }
        if (value === '' || value === null) {
          order[index] = ''
        } else {
          const num = Number(value)
          order[index] = Number.isNaN(num) ? value : num
        }
        this.$set(this.category, 'order', order)
      },
      addCustomOrdering () {
        this.$set(this.customOrdering, this.customOrdering.length, { pos: 0, domains: [] })
      },
      removeCustomOrdering (idx) {
        this.customOrdering = this.customOrdering.filter((_, i) => i !== idx)
      },
      updateCustomOrderingDomains (idx, value) {
        this.$set(this.customOrdering[idx], 'domains', Array.isArray(value) ? value : [])
      },
      buildFormData (payload) {
        const formData = new FormData()
        formData.append('config', JSON.stringify(payload))
        if (this.view === 'edit' && this.category.id) {
          formData.append('categoryId', this.category.id)
        }
        Object.entries(this.uploadFiles || {}).forEach(([key, file]) => {
          if (file instanceof File) {
            formData.append(key, file)
          }
        })
        return formData
      },
      async load () {
        this.loading = true
        this.error = ''
        try {
          const cfg = await this.service.loadConfig()
          this.applyConfig(cfg)
          this.resetUploads()
          this.syncViewFromRoute()
        } catch (e) {
          console.error(e)
          this.error = this.formatError(e) || 'Failed to load configuration'
        } finally {
          this.loading = false
        }
      },
      formatError (e) {
        if (!e) return ''
        if (typeof e === 'string') return e
        if (e.message) return e.message
        if (e.title) return e.title
        if (e.errors) return Object.values(e.errors).flat().join('; ')
        try {
          return JSON.stringify(e)
        } catch (_) {
          return 'Unknown error'
        }
      },
      async save () {
        this.saving = true
        this.error = ''
        try {
          const payload = this.buildPayload()
          const errors = this.validatePayload(payload)
          if (errors.length) {
            this.error = errors.join('; ')
            this.saving = false
            return
          }
          const formData = this.buildFormData(payload)
          const response = await this.service.saveConfig(formData)
          if (response) {
            this.applyConfig(response)
          } else {
            this.categories = payload.categories
          }
          this.resetUploads()
          this.editingCategoryIndex = null
          const isGlobalSettings = this.view === 'global-settings'
          if (!isGlobalSettings && (this.$route.query.categoryId || this.$route.query.new)) {
            await this.$router.push({ path: '/custom-categories' })
          } else if (!isGlobalSettings) {
            this.backToListState()
          }
          try {
            await ClearCacheService.clearEventCategoryConfig()
          } catch (e) {
            console.error('Cache clear failed:', e)
          }
          this.$swal.fire({
            icon: 'success',
            title: isGlobalSettings ? 'Global settings saved successfully' : 'Configuration saved successfully',
            timer: 1500,
            showConfirmButton: false
          })
        } catch (e) {
          console.error(e)
          this.error = this.formatError(e) || 'Failed to save configuration'
        } finally {
          this.saving = false
        }
      },
      onFileChanged (field) {
        if (!this.uploadFiles) return
        let value = this.uploadFiles[field]
        if (value) {
          if (value instanceof File && value.previewUrl) {
            URL.revokeObjectURL(value.previewUrl)
          } else if (Array.isArray(value) && value[0]?.previewUrl) {
            URL.revokeObjectURL(value[0].previewUrl)
          }
        }
        if (Array.isArray(value)) {
          value = value[0] || null
        }
        this.$set(this.uploadFiles, field, value)
      },
      resetUploads () {
        this.revokeAllPreviewUrls()
        this.uploadFiles = {
          customEventGameThumb: null,
          customEventGameThumbWebp: null,
          promoThumbPath: null,
          promoThumbPathWebP: null
        }
      },
      revokeAllPreviewUrls () {
        Object.keys(this.uploadFiles || {}).forEach(key => {
          const file = this.uploadFiles[key]
          if (file instanceof File && file.previewUrl) {
            URL.revokeObjectURL(file.previewUrl)
          } else if (Array.isArray(file) && file[0]?.previewUrl) {
            URL.revokeObjectURL(file[0].previewUrl)
          }
        })
      },
      getPreviewUrl (field) {
        const file = this.uploadFiles?.[field]
        if (!file) return null
        if (file instanceof File) {
          if (!file.previewUrl) {
            file.previewUrl = URL.createObjectURL(file)
          }
          return file.previewUrl
        }
        if (Array.isArray(file) && file.length > 0 && file[0] instanceof File) {
          const firstFile = file[0]
          if (!firstFile.previewUrl) {
            firstFile.previewUrl = URL.createObjectURL(firstFile)
          }
          return firstFile.previewUrl
        }
        return null
      }
    }
  }
</script>

<style scoped lang="scss">
.custom-categories-form {
  .cell-name {
    font-weight: 500;
  }
  .multiple-inputs-wrap {
    margin-bottom: 16px;
    padding: 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
  }
  .category-schedule-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;

    &__column {
      flex: 1 1 240px;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
  }
  .category-id-column {
    flex: 0 1 220px;
    max-width: 280px;
  }
  .form__field-label {
    display: block;
    margin-bottom: 4px;
  }
  .v-tabs {
    margin-bottom: 8px;
  }
  .v-tabs-items {
    margin-bottom: 16px;
  }
  .image-preview {
    margin-top: 12px;
    padding: 8px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #f9fafb;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100px;
  }
  .preview-image {
    max-width: 100%;
    max-height: 200px;
    object-fit: contain;
    border-radius: 4px;
  }
}

::v-deep .domains-combobox .v-text-field {
  padding-top: 0;
  margin-top: 0;
}
::v-deep .domains-combobox .v-input__slot {
  margin-top: 0;
}

.ordering-row__pos {
  max-width: 120px;
}
</style>
